import { QueryError } from '@graphql-box/helpers';

import { useQuery } from '@graphql-box/react';
import { useEnvs } from '@next-box/envs';
import { type ScreenType } from '@tmdb-graphql-api/schema/types';
import { useEffect, useState } from 'react';
import { FeaturePlayer } from '#components/FeaturePlayer/index.tsx';
import { getNextEpisode, getPrevEpisode } from '#components/FeaturePlayerModal/Tv/helpers/getNextPrevEpisode.ts';
import { VideoModal } from '#components/Modal/VideoModal.tsx';
import { useToolbelt } from '#hooks/useToolbelt.ts';
import { useStore } from '#store/useStore.ts';
import { type PathIds } from '#types.ts';
import { EpisodesDrawer } from './EpisodesDrawer.tsx';
import { formatEpisodes } from './helpers/formatEpisodes.ts';
import { type TvSeasonEpisodeData } from './types.ts';

export type FeaturePlayerTvModalProps = {
  id: string | number;
  initialPathIds: PathIds;
  onClose: () => void;
  open: boolean;
  title: string;
  type: ScreenType;
};

const GET_EVERY_EPISODE = "\nquery GetEveryEpisode(\n  $id: ID!\n) {\n  tv(id: $id) {\n    seasons {\n      name\n      episodeCount\n      seasonNumber\n      episodes {\n        airDate\n        episodeNumber\n        name\n        runtime\n        stillPath\n      }\n    }\n  }\n}\n\nfragment SeasonEpisodes on Season {\n  episodes {\n    episodeNumber\n    name\n    stillPath\n    voteAverage\n    voteCount\n  }\n}\n";

export const FeaturePlayerTvModal = ({ initialPathIds, title, type, ...rest }: FeaturePlayerTvModalProps) => {
  const { config } = useToolbelt();
  const { getEnv } = useEnvs();
  const addRecentlyWatchedTv = useStore(store => store.addRecentlyWatchedTv);
  const updateContinueWatching = useStore(store => store.updateContinueWatching);
  const getContinueWatchingTvEntry = useStore(store => store.getContinueWatchingTvEntry);
  // @ts-expect-error TODO: Need to refactor so tvId is string
  const entry = getContinueWatchingTvEntry(initialPathIds[0]);
  const [pathIds, setPathIds] = useState<PathIds>(entry ?? initialPathIds);
  const [episodesDrawOpen, setEpisodesDrawOpen] = useState(false);
  const baseUrl = getEnv('NEXT_PUBLIC_EXOTIC_PINK_PTARMIGAN_PROVIDER') ?? '';
  const [tvId, seasonNumber, episodeNumber] = pathIds;
  const seasonNumberWithDefault = seasonNumber ?? 1;
  const episodeNumberWithDefault = episodeNumber ?? 1;

  const { execute, loading, result } = useQuery<TvSeasonEpisodeData>(GET_EVERY_EPISODE, {
    loading: true,
  });

  useEffect(() => {
    void execute({ variables: { id: tvId } }, { data: { initiator: 'FeaturePlayerTvModal' } });
    // We only want to re-execute when id changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tvId]);

  if (result instanceof QueryError) {
    console.error(result);
    return;
  }

  if (result?.errors?.length) {
    console.error(result.errors);
  }

  const items = formatEpisodes(result?.data, config);
  const hasEpisodes = !loading && items.length > 0;
  const prevEpisode = getPrevEpisode(items, [seasonNumberWithDefault, episodeNumberWithDefault]);
  const nextEpisode = getNextEpisode(items, [seasonNumberWithDefault, episodeNumberWithDefault]);

  return (
    <>
      <VideoModal
        {...rest}
        hasNext={!!nextEpisode}
        hasPrev={!!prevEpisode}
        id={tvId}
        onNext={() => {
          if (nextEpisode) {
            setPathIds([tvId, ...nextEpisode]);
          }
        }}
        onOpenPlaylist={() => {
          setEpisodesDrawOpen(true);
        }}
        onPrev={() => {
          if (prevEpisode) {
            setPathIds([tvId, ...prevEpisode]);
          }
        }}
        playlist={hasEpisodes}
        title={`${title} - S${String(seasonNumberWithDefault)}E${String(episodeNumberWithDefault)}`}
      >
        <FeaturePlayer
          onClick={() => {
            if (nextEpisode) {
              updateContinueWatching(pathIds);
            } else {
              // @ts-expect-error TODO: Need to refactor so tvId is string
              addRecentlyWatchedTv(tvId);
            }
          }}
          url={`${atob(baseUrl)}/${type.toLowerCase()}/${String(tvId)}/${String(seasonNumberWithDefault)}/${String(episodeNumberWithDefault)}`}
        />
      </VideoModal>
      {hasEpisodes ? (
        <EpisodesDrawer
          items={items}
          onEpisodeClick={(seNo, epNo) => {
            setPathIds([tvId, seNo, epNo]);
            setEpisodesDrawOpen(false);
          }}
          open={episodesDrawOpen}
          selectedEpisodeNumber={episodeNumberWithDefault}
          selectedSeasonNumber={seasonNumberWithDefault}
          setOpen={setEpisodesDrawOpen}
        />
      ) : undefined}
    </>
  );
};
